// Exportar a página do quadro (0.65): PDF (desenhado ponto a ponto, nítido em qualquer zoom) ou imagem (PNG),
// pela janela de compartilhar do sistema, com o histórico de relatórios que o app já tem.
import { Platform, type View } from 'react-native';
import { PDFDocument, StandardFonts, rgb, LineCapStyle, type PDFFont, type PDFPage } from 'pdf-lib';
import type { Folha, Quadro as Q } from '../store/types';
import { limparTexto } from './pdfTexto';
import { salvarECompartilhar } from './arquivoSalvar';
import { nomeArquivo } from './pdf';
import { temLocal } from './anexos';
import { t } from './i18n';
import { baseDaLinha, medidasTexto, segmentos, yAssentado } from './textoFolha';
import type { QuadroTexto } from '../store/types';

type Opcoes = { tipo: 'pdf' | 'imagem'; nome: string; fundo: string; escura: boolean; folha: Folha; conteudo: View | null };

function cor(c: string): { c: ReturnType<typeof rgb>; a: number } {
  const m = c.match(/rgba?\(([^)]+)\)/i);
  if (m) { const [r, g, b, a] = m[1].split(',').map(x => parseFloat(x)); return { c: rgb(r / 255, g / 255, b / 255), a: isNaN(a) ? 1 : a }; }
  const h = c.replace('#', '');
  const n = parseInt(h.length === 3 ? h.split('').map(x => x + x).join('') : h.slice(0, 6), 16) || 0;
  return { c: rgb(((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255), a: 1 };
}
const PRETOS = ['#111827', '#000000', '#111111', '#0F172A'];
const tinta = (c: string | undefined, escura: boolean) => { const x = c ?? '#111827'; return escura && PRETOS.includes(x.toUpperCase()) ? '#F5F5F5' : x; };

function quebrar(texto: string, f: PDFFont, tam: number, largura: number) {
  const out: string[] = [];
  for (const par of limparTexto(texto).split('\n')) {
    let linha = '';
    for (const p of par.split(/\s+/)) {
      const teste = linha ? linha + ' ' + p : p;
      if (f.widthOfTextAtSize(teste, tam) > largura && linha) { out.push(linha); linha = p; } else linha = teste;
    }
    out.push(linha);
  }
  return out;
}

type Fontes = { n: PDFFont; b: PDFFont; i: PDFFont; bi: PDFFont };
type Pedaco = { txt: string; f: PDFFont; tam: number; cor?: string; u: boolean; w: number };
/** 0.72 (texto 11b B5): texto com trechos de estilo próprio (tamanho, N, I, S, cor) quebrado em linhas na largura da caixa. */
function linhasRicas(x: QuadroTexto, F: Fontes, k: number, largura: number): Pedaco[][] {
  const m = medidasTexto(x);
  const linhas: Pedaco[][] = [[]];
  let w = 0;
  for (const g of segmentos(x)) {
    const b = g.e.b ?? !!x.b, it = g.e.i ?? !!x.i;
    const f = b && it ? F.bi : b ? F.b : it ? F.i : F.n, tam = (g.e.tam ?? m.f) * k;
    for (const tk of g.texto.split(/(\n|\s+)/)) {
      if (!tk) continue;
      if (tk === '\n') { linhas.push([]); w = 0; continue; }
      const txt = limparTexto(tk), tw = f.widthOfTextAtSize(txt, tam);
      const espaco = /^\s+$/.test(tk);
      if (!espaco && w + tw > largura && w > 0) { linhas.push([]); w = 0; }
      if (espaco && w === 0) continue;
      linhas[linhas.length - 1].push({ txt, f, tam, cor: g.e.cor ?? x.cor, u: g.e.u ?? !!x.u, w: tw });
      w += tw;
    }
  }
  return linhas;
}

async function bytesDe(uri: string): Promise<Uint8Array | null> {
  try {
    if (Platform.OS === 'web') { const r = await fetch(uri); return new Uint8Array(await r.arrayBuffer()); }
    const { File } = require('expo-file-system');
    return await new File(uri).bytes();
  } catch { return null; }
}

/** Monta o PDF da folha inteira (1 página do tamanho da folha usada). */
export async function pdfDoQuadro(q: Q, o: Pick<Opcoes, 'fundo' | 'escura' | 'folha'>): Promise<Uint8Array> {
  const doc = await PDFDocument.create();
  const F = {
    n: await doc.embedFont(StandardFonts.Helvetica), b: await doc.embedFont(StandardFonts.HelveticaBold),
    i: await doc.embedFont(StandardFonts.HelveticaOblique), bi: await doc.embedFont(StandardFonts.HelveticaBoldOblique),
  };
  // só a área usada (com folga), para não sair uma folha enorme vazia
  let mx = 600, my = 400;
  for (const tr of q.tracos) for (const m of tr.d.matchAll(/[ML](-?[\d.]+) (-?[\d.]+)/g)) { mx = Math.max(mx, +m[1]); my = Math.max(my, +m[2]); }
  for (const x of [...q.textos, ...q.checks, ...(q.tabelas ?? [])]) { mx = Math.max(mx, x.x + x.w); my = Math.max(my, x.y + 160); }
  for (const im of q.imagens ?? []) { mx = Math.max(mx, im.x + im.w); my = Math.max(my, im.y + im.h); }
  const larg = Math.max(1000, Math.min(q.larg, mx + 60)), alt = Math.max(1414, Math.min(q.alt, my + 80));
  const k = 595.28 / 1000;                      // 1000 unidades = largura de uma folha A4
  const W = larg * k, H = alt * k;
  const pg: PDFPage = doc.addPage([W, H]);
  const Y = (y: number) => H - y * k;
  // fundo e linhas
  const fd = cor(o.fundo);
  pg.drawRectangle({ x: 0, y: 0, width: W, height: H, color: fd.c });
  const linha = o.escura ? rgb(0.35, 0.37, 0.42) : rgb(0.78, 0.82, 0.9);
  if (o.folha.tipo === 'pautada' || o.folha.tipo === 'margem') for (let y = 80 + 39; y < alt; y += 40) pg.drawLine({ start: { x: 0, y: Y(y) }, end: { x: W, y: Y(y) }, thickness: 0.6, color: linha });
  if (o.folha.tipo === 'margem') pg.drawLine({ start: { x: 90 * k, y: 0 }, end: { x: 90 * k, y: H }, thickness: 0.9, color: rgb(0.9, 0.55, 0.55) });
  if (o.folha.tipo === 'quadriculada') {
    for (let y = 40; y < alt; y += 40) pg.drawLine({ start: { x: 0, y: Y(y) }, end: { x: W, y: Y(y) }, thickness: 0.4, color: linha });
    for (let x = 40; x < larg; x += 40) pg.drawLine({ start: { x: x * k, y: 0 }, end: { x: x * k, y: H }, thickness: 0.4, color: linha });
  }
  if (o.folha.tipo === 'pontilhada') for (let y = 20; y < alt; y += 40) for (let x = 20; x < larg; x += 40) pg.drawCircle({ x: x * k, y: Y(y), size: 0.9, color: linha });
  // imagens
  for (const im of q.imagens ?? []) {
    const src = im.arq.dado ?? (temLocal(im.arq) ? im.arq.uri : undefined);
    const b = src ? await bytesDe(src) : null;
    try {
      if (!b) throw new Error('sem');
      const png = im.arq.mime?.includes('png') || src?.toLowerCase().endsWith('.png');
      const img = png ? await doc.embedPng(b) : await doc.embedJpg(b);
      pg.drawImage(img, { x: im.x * k, y: Y(im.y + im.h), width: im.w * k, height: im.h * k });
    } catch {
      pg.drawRectangle({ x: im.x * k, y: Y(im.y + im.h), width: im.w * k, height: im.h * k, borderColor: linha, borderWidth: 0.8 });
      pg.drawText(limparTexto(t('Imagem não encontrada')), { x: im.x * k + 6, y: Y(im.y + 24), size: 9, font: F.n, color: rgb(0.5, 0.5, 0.5) });
    }
  }
  // traços
  for (const tr of q.tracos) {
    const c = cor(tinta(tr.cor, o.escura));
    pg.drawSvgPath(tr.d, { x: 0, y: H, scale: k, borderColor: c.c, borderOpacity: c.a, borderWidth: tr.w * k, borderLineCap: LineCapStyle.Round });
  }
  const txtCor = (c?: string) => cor(tinta(c, o.escura)).c;
  // textos
  for (const x of q.textos) {
    if (!x.texto.trim()) continue;
    if (x.trechos?.length) {
      // com trechos: cada pedaço com a própria letra; a linha é a da maior letra (como na tela)
      const m = medidasTexto(x), y0 = yAssentado(x, o.folha) + baseDaLinha(m.fm, m.l);
      linhasRicas(x, F, k, x.w * k - 6).forEach((l, i) => {
        const yy = Y(y0 + i * m.l);
        let xx = x.x * k + 3;
        for (const pd of l) {
          pg.drawText(pd.txt, { x: xx, y: yy, size: pd.tam, font: pd.f, color: txtCor(pd.cor) });
          if (pd.u && pd.txt.trim()) pg.drawLine({ start: { x: xx, y: yy - 1.5 }, end: { x: xx + pd.w, y: yy - 1.5 }, thickness: 0.6, color: txtCor(pd.cor) });
          xx += pd.w;
        }
      });
      continue;
    }
    // G3: mesmas medidas da tela (letra e linha) e a base das letras assentada na linha da folha
    const m = medidasTexto(x), tam = m.f * k, f = x.b && x.i ? F.bi : x.b ? F.b : x.i ? F.i : F.n;
    const y0 = yAssentado(x, o.folha) + baseDaLinha(m.f, m.l);
    quebrar(x.texto, f, tam, x.w * k - 6).forEach((l, i) => {
      const yy = Y(y0 + i * m.l);
      pg.drawText(l, { x: x.x * k + 3, y: yy, size: tam, font: f, color: txtCor(x.cor) });
      if (x.u && l) pg.drawLine({ start: { x: x.x * k + 3, y: yy - 1.5 }, end: { x: x.x * k + 3 + f.widthOfTextAtSize(l, tam), y: yy - 1.5 }, thickness: 0.6, color: txtCor(x.cor) });
    });
  }
  // checklists
  for (const c of q.checks) {
    let y = c.y + 8;
    for (const it of c.itens) {
      const tam = 36 * k * 0.92;
      pg.drawRectangle({ x: c.x * k + 4, y: Y(y + 30), width: 30 * k, height: 30 * k, borderColor: txtCor(), borderWidth: 0.8, ...(it.done ? { color: rgb(0.45, 0.4, 0.9) } : {}) });
      quebrar(it.text, F.n, tam, c.w * k - 40 * k).forEach((l, i) => pg.drawText(l, { x: c.x * k + 44 * k, y: Y(y) - tam - i * tam * 1.3, size: tam, font: F.n, color: txtCor() }));
      y += Math.max(44, 50 * Math.max(1, quebrar(it.text, F.n, tam, c.w * k - 40 * k).length));
    }
  }
  // tabelas
  for (const tb of q.tabelas ?? []) {
    const cols = tb.linhas[0]?.length ?? 1, cw = tb.w / cols, ch = 50;
    tb.linhas.forEach((l, r) => l.forEach((v, c) => {
      const tam = ((tb.tams?.[`${r}-${c}`] ?? 34) * 30 / 34) * k;   // 0.72: tamanho da letra de cada célula
      const x0 = (tb.x + c * cw) * k, y0 = Y(tb.y + (r + 1) * ch);
      pg.drawRectangle({ x: x0, y: y0, width: cw * k, height: ch * k, borderColor: linha, borderWidth: 0.7 });
      const txt = quebrar(v, r === 0 ? F.b : F.n, tam, cw * k - 6)[0] ?? '';
      pg.drawText(txt, { x: x0 + 3, y: y0 + (ch * k - tam) / 2, size: tam, font: r === 0 ? F.b : F.n, color: txtCor() });
    }));
  }
  return doc.save();
}

/** Exporta pela janela de compartilhar. Imagem: foto do quadro inteiro (app novo); sem o módulo, vai em PDF. */
export async function exportarQuadro(q: Q, o: Opcoes) {
  const nome = nomeArquivo(o.nome);
  if (o.tipo === 'imagem' && Platform.OS !== 'web') {
    try {
      const { captureRef } = require('react-native-view-shot');
      const uri: string = await captureRef(o.conteudo, { format: 'png', quality: 1, result: 'tmpfile' });
      const b = await bytesDe(uri);
      if (b) return salvarECompartilhar(b, nome + '.png', 'image/png');
    } catch { /* app antigo, sem o módulo: segue em PDF */ }
  }
  const bytes = await pdfDoQuadro(q, o);
  await salvarECompartilhar(bytes, nome + '.pdf', 'application/pdf');
}
